import React, { useState } from 'react'
import { Claim, InsurancePayment } from '@dataTypes/claimFeature.types'
import { Button, InputField } from '@components/common'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { isClaimApproved } from '@utils/claimUtils'
import './AddClaimsStyle.css'

type ClaimInsurancePaymentProps = {
  selectedClaim: Claim
  insurancePayments: InsurancePayment[]
  onUpdateInsurancePayments: (updatedPayments: InsurancePayment[]) => void
}

type PaymentFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
}

const PaymentField: React.FC<PaymentFieldProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}) => (
  <InputField
    ariaLabel={label}
    value={value}
    type={type}
    placeholder={placeholder}
    className="form-control"
    onChange={(event) => onChange(event.target.value)}
  />
)

const ClaimInsurancePayment: React.FC<ClaimInsurancePaymentProps> = ({
  selectedClaim,
  insurancePayments,
  onUpdateInsurancePayments,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [newPayment, setNewPayment] = useState<InsurancePayment | null>(null)

  const isApproved = isClaimApproved(selectedClaim)
  const handleEditPayment = (index: number): void => {
    setEditingIndex(index)
  }

  const handleChangePayment = (
    index: number,
    field: keyof InsurancePayment,
    value: string,
  ): void => {
    const updatedPayments = [...insurancePayments]
    updatedPayments[index] = { ...updatedPayments[index], [field]: value }
    onUpdateInsurancePayments(updatedPayments)
  }

  const handleSavePayment = (): void => {
    setEditingIndex(null)
  }

  const handleDeletePayment = (index: number): void => {
    const updatedPayments = insurancePayments.filter((_, i) => i !== index)
    onUpdateInsurancePayments(updatedPayments)
  }

  const handleAddPaymentRow = (): void => {
    setNewPayment({
      id: crypto.randomUUID(),
      checkNumber: '',
      bankName: '',
      depositDate: '',
      eobNumber: '',
    })
  }

  const handleSaveNewPayment = (): void => {
    if (
      newPayment &&
      newPayment.checkNumber &&
      newPayment.bankName &&
      newPayment.depositDate &&
      newPayment.eobNumber
    ) {
      const updatedPayments = [...insurancePayments, newPayment]
      onUpdateInsurancePayments(updatedPayments)
      setNewPayment(null)
    }
  }

  return (
    <div className={isApproved ? 'disabled-claims-modal mt-2' : ''}>
      {!newPayment && (
        <div className="text-end mb-2">
          <Button
            type="button"
            onClick={handleAddPaymentRow}
            className="button-primary button-claim-add-insurance-paayment"
          >
            Add Payment
          </Button>
        </div>
      )}

      <div className="claim-insurance-payment-table-custom">
        <table className="table mb-0">
          <thead className="cipt-custom">
            <tr>
              <th scope="col">Check #</th>
              <th scope="col">Bank name</th>
              <th scope="col">Deposit date</th>
              <th scope="col">EOB #</th>
              <th scope="col" className="actions-column text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {newPayment && (
              <tr>
                <td>
                  <PaymentField
                    label="Check #"
                    placeholder="Check #"
                    value={newPayment.checkNumber}
                    onChange={(value) =>
                      setNewPayment({
                        ...newPayment,
                        checkNumber: value,
                      })
                    }
                  />
                </td>
                <td>
                  <PaymentField
                    label="Bank name"
                    placeholder="Bank #"
                    value={newPayment.bankName}
                    onChange={(value) =>
                      setNewPayment({
                        ...newPayment,
                        bankName: value,
                      })
                    }
                  />
                </td>
                <td>
                  <PaymentField
                    label="Deposit date"
                    type="date"
                    value={newPayment.depositDate}
                    onChange={(value) =>
                      setNewPayment({
                        ...newPayment,
                        depositDate: value,
                      })
                    }
                  />
                </td>
                <td>
                  <PaymentField
                    label="EOB #"
                    placeholder="EOB #"
                    value={newPayment.eobNumber}
                    onChange={(value) =>
                      setNewPayment({
                        ...newPayment,
                        eobNumber: value,
                      })
                    }
                  />
                </td>
                <td className="actions-column text-nowrap">
                  <div className="action-buttons-container d-flex align-items-center">
                    <button
                      className="action-button"
                      title="Save"
                      onClick={handleSaveNewPayment}
                    >
                      <i className="ti ti-check text-success fs-4"></i>
                    </button>
                    <button
                      className="action-button"
                      title="Cancel"
                      onClick={() => setNewPayment(null)}
                    >
                      <i className="ti ti-x text-danger fs-4"></i>
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {insurancePayments.length > 0 ? (
              insurancePayments.map((payment, index) => (
                <tr key={index}>
                  <td>
                    {editingIndex === index ? (
                      <PaymentField
                        label="Check #"
                        value={payment.checkNumber}
                        onChange={(value) =>
                          handleChangePayment(index, 'checkNumber', value)
                        }
                      />
                    ) : (
                      payment.checkNumber || '-'
                    )}
                  </td>
                  <td>
                    {editingIndex === index ? (
                      <PaymentField
                        label="Bank name"
                        value={payment.bankName}
                        onChange={(value) =>
                          handleChangePayment(index, 'bankName', value)
                        }
                      />
                    ) : (
                      payment.bankName || '-'
                    )}
                  </td>
                  <td>
                    {editingIndex === index ? (
                      <PaymentField
                        label="Deposit date"
                        type="date"
                        value={payment.depositDate}
                        onChange={(value) =>
                          handleChangePayment(index, 'depositDate', value)
                        }
                      />
                    ) : (
                      payment.depositDate || '-'
                    )}
                  </td>
                  <td>
                    {editingIndex === index ? (
                      <PaymentField
                        label="EOB #"
                        value={payment.eobNumber}
                        onChange={(value) =>
                          handleChangePayment(index, 'eobNumber', value)
                        }
                      />
                    ) : (
                      payment.eobNumber || '-'
                    )}
                  </td>
                  <td className="actions-column text-nowrap">
                    <div className="action-buttons-container d-flex align-items-center">
                      {editingIndex === index ? (
                        <Button
                          className="action-button"
                          onClick={handleSavePayment}
                        >
                          <i className="ti ti-check text-success fs-4"></i>
                        </Button>
                      ) : (
                        <ActionButtons
                          onEdit={() => handleEditPayment(index)}
                          onDelete={() => handleDeletePayment(index)}
                          showView={false}
                          showMoreOptions={false}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No insurance payments recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ClaimInsurancePayment
